import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { LogOut, Inbox, MapPinned, Star, Trash2, Mail, Phone, Loader2, X, Pencil, Plus, BarChart3, Ticket, Globe, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/brand";
import TourEditor from "@/components/admin/TourEditor";
import AdminAnalytics from "@/components/admin/AdminAnalytics";
import PromoCodesPanel from "@/components/admin/PromoCodesPanel";
import AdminOtaDistribution from "@/components/admin/AdminOtaDistribution";
import AdminPagesEditor from "@/components/admin/AdminPagesEditor";
import AdminCustomPagesEditor from "@/components/admin/AdminCustomPagesEditor";
import AdminThemesEditor from "@/components/admin/AdminThemesEditor";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const STATUSES = ["new", "contacted", "converted", "archived"];
const STATUS_COLORS = {
  new: "bg-saffron text-white",
  contacted: "bg-peacock text-white",
  converted: "bg-emerald-600 text-white",
  archived: "bg-gray-400 text-white",
};
const INQUIRY_TYPES = [
  { value: "all", label: "All types" },
  { value: "wedding", label: "Wedding" },
  { value: "mock-wedding", label: "Mock Wedding" },
  { value: "honeymoon", label: "Honeymoon" },
  { value: "general", label: "General" },
];
const INQUIRY_TYPE_LABELS = { wedding: "Wedding", "mock-wedding": "Mock Wedding", honeymoon: "Honeymoon" };
const INQUIRY_TYPE_COLORS = {
  wedding: "bg-deepred/10 text-deepred",
  "mock-wedding": "bg-gold/20 text-gold-600",
  honeymoon: "bg-pink-100 text-pink-700",
  general: "bg-cream-200 text-foreground/70",
};

export default function Admin() {
  const { user, logout } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [tours, setTours] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [inquiryTypeFilter, setInquiryTypeFilter] = useState("all");
  const [editorState, setEditorState] = useState({ open: false, mode: "create", slug: null });

  const openCreateEditor = () => setEditorState({ open: true, mode: "create", slug: null });
  const openEditEditor = (slug) => setEditorState({ open: true, mode: "edit", slug });
  const closeEditor = () => setEditorState((s) => ({ ...s, open: false }));

  const refreshTours = async () => {
    try {
      const t = await api.get("/tours");
      setTours(t.data);
    } catch (e) {
      toast.error("Failed to refresh tours");
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [i, t, r] = await Promise.all([
        api.get("/admin/inquiries"),
        api.get("/tours"),
        api.get("/testimonials"),
      ]);
      setInquiries(i.data);
      setTours(t.data);
      setTestimonials(r.data);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success("Status updated");
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const deleteTour = async (slug) => {
    if (!window.confirm(`Delete tour "${slug}"?`)) return;
    try {
      await api.delete(`/admin/tours/${slug}`);
      setTours((prev) => prev.filter((t) => t.slug !== slug));
      toast.success("Tour deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      setTestimonials((prev) => prev.filter((t) => t.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const stats = [
    { label: "Total Inquiries", value: inquiries.length, color: "saffron" },
    { label: "New Today", value: inquiries.filter((i) => i.status === "new").length, color: "deepred" },
    { label: "Active Tours", value: tours.length, color: "peacock" },
    { label: "Testimonials", value: testimonials.length, color: "gold" },
  ];

  const filteredInquiries = useMemo(() => {
    if (inquiryTypeFilter === "all") return inquiries;
    if (inquiryTypeFilter === "general") return inquiries.filter((i) => !i.inquiry_type);
    return inquiries.filter((i) => i.inquiry_type === inquiryTypeFilter);
  }, [inquiries, inquiryTypeFilter]);

  return (
    <div className="min-h-screen bg-cream-100" data-testid="admin-dashboard">
      {/* TOP BAR */}
      <header className="bg-white border-b border-cream-200 sticky top-0 z-30">
        <div className="container-tti h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="TTI Holidays" className="h-14 w-14 object-contain rounded-sm" />
            <div className="hidden sm:block">
              <div className="text-sm font-medium">Admin Console</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-saffron">
                {user?.email}
              </div>
            </div>
          </Link>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-deepred"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="container-tti py-12">
        <div className="mb-10">
          <div className="eyebrow mb-2">Dashboard</div>
          <h1 className="font-serif text-4xl">Namaste, {user?.name || "Admin"}.</h1>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {stats.map((s) => (
            <div key={s.label} className="bg-white border border-cream-200 p-6" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">{s.label}</div>
              <div className="font-serif text-4xl mt-2">{s.value}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="bg-white border border-cream-200" data-testid="admin-tabs">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard"><BarChart3 className="w-4 h-4 mr-2" /> Dashboard</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries"><Inbox className="w-4 h-4 mr-2" /> Inquiries</TabsTrigger>
            <TabsTrigger value="pages" data-testid="tab-pages"><FileText className="w-4 h-4 mr-2" /> Pages</TabsTrigger>
            <TabsTrigger value="themes" data-testid="tab-themes"><FileText className="w-4 h-4 mr-2" /> Themes</TabsTrigger>
            <TabsTrigger value="custom-pages" data-testid="tab-custom-pages"><FileText className="w-4 h-4 mr-2" /> Custom Pages</TabsTrigger>
            <TabsTrigger value="tours" data-testid="tab-tours"><MapPinned className="w-4 h-4 mr-2" /> Tours</TabsTrigger>
            <TabsTrigger value="promos" data-testid="tab-promos"><Ticket className="w-4 h-4 mr-2" /> Promo codes</TabsTrigger>
            <TabsTrigger value="ota" data-testid="tab-ota"><Globe className="w-4 h-4 mr-2" /> OTA distribution</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials"><Star className="w-4 h-4 mr-2" /> Testimonials</TabsTrigger>
          </TabsList>

          {/* DASHBOARD */}
          <TabsContent value="dashboard">
            <AdminAnalytics />
          </TabsContent>

          {/* PAGES EDITOR */}
          <TabsContent value="pages">
            <AdminPagesEditor />
          </TabsContent>

          {/* THEMES EDITOR */}
          <TabsContent value="themes">
            <AdminThemesEditor />
          </TabsContent>

          <TabsContent value="custom-pages">
            <AdminCustomPagesEditor />
          </TabsContent>

          {/* INQUIRIES */}
          <TabsContent value="inquiries">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground/60">Filter by type:</span>
                <Select value={inquiryTypeFilter} onValueChange={setInquiryTypeFilter}>
                  <SelectTrigger className="w-44 h-9 bg-white" data-testid="inquiry-type-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INQUIRY_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-xs text-foreground/50" data-testid="inquiry-filter-count">
                {filteredInquiries.length} of {inquiries.length} inquiries
              </div>
            </div>
            <div className="bg-white border border-cream-200">
              {loading ? (
                <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
              ) : filteredInquiries.length === 0 ? (
                <div className="p-12 text-center text-foreground/60" data-testid="inquiries-empty-state">
                  {inquiries.length === 0 ? "No inquiries yet." : "No inquiries match this filter."}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Tour</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInquiries.map((i) => (
                      <TableRow key={i.id} className="hover:bg-cream-100 cursor-pointer" data-testid={`inquiry-row-${i.id}`}>
                        <TableCell className="text-xs text-foreground/60">
                          {new Date(i.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell onClick={() => setSelectedInquiry(i)}>
                          <div className="font-medium">{i.name}</div>
                          <div className="text-xs text-foreground/60">{i.country || "—"}</div>
                        </TableCell>
                        <TableCell onClick={() => setSelectedInquiry(i)}>
                          <div className="text-xs">{i.email}</div>
                          <div className="text-xs text-foreground/60">{i.phone}</div>
                        </TableCell>
                        <TableCell onClick={() => setSelectedInquiry(i)} className="text-sm">{i.tour_interest || "—"}</TableCell>
                        <TableCell onClick={() => setSelectedInquiry(i)}>
                          <span
                            className={`text-[10px] uppercase tracking-wider font-medium px-2 py-1 rounded-sm ${INQUIRY_TYPE_COLORS[i.inquiry_type || "general"]}`}
                            data-testid={`inquiry-type-badge-${i.id}`}
                          >
                            {INQUIRY_TYPE_LABELS[i.inquiry_type] || "General"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Select value={i.status} onValueChange={(v) => updateStatus(i.id, v)}>
                            <SelectTrigger className={`w-32 h-8 text-xs ${STATUS_COLORS[i.status] || ""}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => deleteInquiry(i.id)}
                            data-testid={`delete-inquiry-${i.id}`}
                            className="text-foreground/40 hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* TOURS */}
          <TabsContent value="tours">
            <div className="bg-white border border-cream-200">
              <div className="flex items-center justify-between p-4 border-b border-cream-200">
                <div>
                  <div className="text-sm font-medium">All tours</div>
                  <div className="text-xs text-foreground/60">{tours.length} tour{tours.length === 1 ? "" : "s"} in catalog</div>
                </div>
                <button
                  onClick={openCreateEditor}
                  data-testid="create-tour-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-saffron hover:bg-deepred text-white rounded-sm text-sm font-medium uppercase tracking-wider transition-colors"
                >
                  <Plus className="w-4 h-4" /> Create new tour
                </button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead></TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tours.map((t) => (
                    <TableRow key={t.slug} data-testid={`tour-row-${t.slug}`}>
                      <TableCell>
                        <img src={t.image_url} alt={t.title} className="w-12 h-12 object-cover rounded-sm" />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{t.title}</div>
                        <div className="text-xs text-foreground/60">/{t.slug}</div>
                      </TableCell>
                      <TableCell className="text-xs uppercase tracking-wider">{t.category}</TableCell>
                      <TableCell className="text-sm">{t.duration}</TableCell>
                      <TableCell className="text-sm font-medium">
                        <div>${(t.price_usd || 0).toLocaleString("en-US")}</div>
                        <div className="text-xs text-foreground/60">₹{(t.price_from || 0).toLocaleString("en-IN")}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-3">
                          <button
                            onClick={() => openEditEditor(t.slug)}
                            data-testid={`edit-tour-${t.slug}`}
                            title="Edit tour"
                            className="text-foreground/50 hover:text-saffron transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteTour(t.slug)}
                            data-testid={`delete-tour-${t.slug}`}
                            title="Delete tour"
                            className="text-foreground/40 hover:text-destructive transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* PROMO CODES */}
          <TabsContent value="promos">
            <PromoCodesPanel />
          </TabsContent>

          {/* OTA DISTRIBUTION */}
          <TabsContent value="ota">
            <AdminOtaDistribution />
          </TabsContent>

          {/* TESTIMONIALS */}
          <TabsContent value="testimonials">
            <div className="grid md:grid-cols-2 gap-4">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-white border border-cream-200 p-6 relative" data-testid={`testimonial-admin-${t.id}`}>
                  <button
                    onClick={() => deleteTestimonial(t.id)}
                    className="absolute top-4 right-4 text-foreground/40 hover:text-destructive"
                    data-testid={`delete-testimonial-${t.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-3 mb-3">
                    <img src={t.avatar_url} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-foreground/60">{t.location} · {t.tour_name}</div>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-3">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-saffron text-saffron" />
                    ))}
                  </div>
                  <p className="text-sm font-light text-foreground/80 leading-relaxed">{t.text}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Inquiry detail modal */}
      {selectedInquiry && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedInquiry(null)}
          data-testid="inquiry-detail-modal"
        >
          <div
            className="bg-white max-w-xl w-full p-8 rounded-sm relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedInquiry(null)}
              className="absolute top-4 right-4 text-foreground/40 hover:text-foreground"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="eyebrow mb-2">Inquiry</div>
            <h2 className="font-serif text-3xl mb-1">{selectedInquiry.name}</h2>
            <div className="text-sm text-foreground/60 mb-6">{new Date(selectedInquiry.created_at).toLocaleString()}</div>

            <div className="space-y-3 text-sm">
              <div className="flex gap-3 items-center">
                <Mail className="w-4 h-4 text-saffron" />
                <a href={`mailto:${selectedInquiry.email}`} className="hover:text-saffron">{selectedInquiry.email}</a>
              </div>
              <div className="flex gap-3 items-center">
                <Phone className="w-4 h-4 text-saffron" />
                <a href={`tel:${selectedInquiry.phone}`} className="hover:text-saffron">{selectedInquiry.phone}</a>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-cream-200">
                <div><div className="text-xs uppercase tracking-wider text-foreground/60">Country</div><div>{selectedInquiry.country || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-foreground/60">Tour</div><div>{selectedInquiry.tour_interest || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-foreground/60">Travelers</div><div>{selectedInquiry.travelers || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-foreground/60">Travel Date</div><div>{selectedInquiry.travel_date || "—"}</div></div>
              </div>
              {selectedInquiry.message && (
                <div className="pt-3 border-t border-cream-200">
                  <div className="text-xs uppercase tracking-wider text-foreground/60 mb-2">Message</div>
                  <p className="text-sm font-light leading-relaxed">{selectedInquiry.message}</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-8">
              <a
                href={`mailto:${selectedInquiry.email}`}
                className="btn-primary flex-1"
              >Reply by email</a>
              <a
                href={`https://wa.me/${selectedInquiry.phone.replace(/[^\d]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline flex-1"
              >WhatsApp</a>
            </div>
          </div>
        </div>
      )}

      {/* Tour editor drawer */}
      <TourEditor
        open={editorState.open}
        mode={editorState.mode}
        slug={editorState.slug}
        onClose={closeEditor}
        onSaved={refreshTours}
      />
    </div>
  );
}
